import { useEffect, useState, useCallback, useMemo } from 'react';
import { useMount, useSessionStorage, useUnmount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Space } from 'antd';

import PageHeader from '../../uiKitComponents/pageHeader';
import Table from '../../uiKitComponents/table';
import Form from '../../uiKitComponents/form';
import Select from '../../uiKitComponents/select';
import Alert from '../../uiKitComponents/alert';
import Button from '../../uiKitComponents/button';
import PaginationSummary from '../../components/PaginationSummary';

import {
  setCleanOnUnmountTrue,
  getPadsCountry,
  cleanReports,
} from '../../store/actions/reportPagesAction';
import {
  selectCountriesOptions,
  selectUSStatesOptions,
} from '../../store/selectors/bootstrapSelector';
import buildTableColumns from '../../utils/buildTableColumns';

const initialState = {
  page: 1,
  country_codes: [],
  state_codes: [],
};

const PadsCountryPage = () => {
  const [form] = Form.useForm();

  const [state, setState] = useSessionStorage('pads-country', {
    ...initialState,
  });
  const { page, country_codes, state_codes } = state;

  const [error, setError] = useState();
  const [loading, setLoading] = useState(false);

  const { cleanOnUnmount, padsCountry, countries, USStates } = useSelector(
    (store) => ({
      cleanOnUnmount: store.reportPages.cleanOnUnmount,
      padsCountry: store.reportPages.payload.padsCountry,
      countries: selectCountriesOptions(store),
      USStates: selectUSStatesOptions(store),
    }),
  );

  const dispatch = useDispatch();

  useMount(() => {
    setCleanOnUnmountTrue()(dispatch);
    form.setFieldsValue({
      country_codes,
      state_codes,
    });
  });

  const fetchPadCountry = useCallback(() => {
    setLoading(true);

    getPadsCountry({
      country_codes,
      state_codes,
    })(dispatch)
      .catch((err) => {
        if (err?.response?.status === 500) {
          setError({
            status: err.response.status,
            message: err.response.data.message,
          });
          sessionStorage.removeItem('pads-country');
        }
      })
      .finally(() => setLoading(false));
  }, [dispatch, country_codes, state_codes]);

  useEffect(() => {
    fetchPadCountry();
  }, [dispatch, fetchPadCountry]);

  useUnmount(() => {
    if (cleanOnUnmount) {
      sessionStorage.removeItem('pads-country');
      cleanReports()(dispatch);
    }
  });

  const columns = useMemo(() => buildTableColumns(padsCountry?.columns), [
    padsCountry,
  ]);

  const pagination = useMemo(() => {
    const pageSize = 25;
    const total = padsCountry?.data?.length || 0;

    return {
      page,
      pageSize,
      total,
      from: (page - 1) * pageSize + 1,
      to: Math.min(page * pageSize, total),
    };
  }, [padsCountry, page]);

  const onTableChange = (tablePagination, _, __, { action }) => {
    const newState = {};

    if (action === 'paginate') {
      newState.page = tablePagination.current;
    }

    setState((prevState) => ({ ...prevState, ...newState }));
  };

  const onFinish = (values) => {
    const { country_codes: codes, state_codes: states } = values;
    setState((prevState) => ({
      ...prevState,
      country_codes: codes,
      state_codes: states,
    }));
  };

  const onReset = () => {
    form.resetFields();
    setState(initialState);
  };

  if (error)
    return (
      <PageHeader className="page-main-header" title="Pad Country">
        <Alert
          showIcon
          type="error"
          message={`Error ${error.status}`}
          description={error.message}
        />
      </PageHeader>
    );

  return (
    <div>
      <PageHeader className="page-main-header" title="Pad Country">
        <Card className="header-card">
          <h3>Select Countries</h3>
          <Form
            className="d-flex justify-content-between"
            form={form}
            onFinish={onFinish}
            onReset={onReset}
          >
            <Space>
              <Form.Item name="country_codes">
                <Select
                  mode="multiple"
                  placeholder="Choose..."
                  options={countries}
                  optionFilterProp="label"
                  style={{
                    minWidth: 300,
                  }}
                />
              </Form.Item>
              <Form.Item dependencies={['country_codes']} noStyle>
                {({ getFieldValue }) => {
                  const selectedCountries = getFieldValue('country_codes');

                  if (
                    selectedCountries?.length === 1 &&
                    selectedCountries.includes('US')
                  ) {
                    return (
                      <Form.Item name="state_codes" label="Select US States">
                        <Select
                          mode="multiple"
                          placeholder="Choose..."
                          options={USStates}
                          optionFilterProp="label"
                          style={{
                            minWidth: 300,
                          }}
                        />
                      </Form.Item>
                    );
                  }

                  return null;
                }}
              </Form.Item>
            </Space>
            <Form.Item>
              <Space>
                <Button type="dashed" htmlType="reset">
                  Clear
                </Button>
                <Button type="primary" htmlType="submit">
                  Select
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </PageHeader>
      <div className="page-main-content">
        <PaginationSummary meta={pagination} />
        <Table
          columns={columns}
          rowKey="country"
          dataSource={padsCountry?.data}
          loading={loading}
          onChange={onTableChange}
          pagination={{
            position: ['topRight', 'bottomRight'],
            showSizeChanger: false,
            showQuickJumper: true,
            pageSize: pagination.pageSize,
            total: padsCountry?.meta?.total,
            current: pagination.page,
          }}
        />
        <PaginationSummary meta={pagination} />
      </div>
    </div>
  );
};

export default PadsCountryPage;
